import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import AddIcon from "@material-ui/icons/Add";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useDialogStyles } from "../../theme/redesign";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  RecordCard,
  RecordsCardsGrid,
  useViewMode,
  ViewModeToggle,
} from "../../components/ViewModeToggle";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { initialsOf, matchesQuery, publicFileUrl } from "./helpers";
import { BotMarkup, DialogClose, EmptyHint, FilterChips, HintTip, InfoDialog, LoadingHint, NegocioPager, PageFrame, RelatedLinks, SearchField, StatusChip, stopRow, tNegocio, usePager } from "./shared";
import NegocioMessageEditor from "./MessageBox";
import { ProductMark } from "./AccountPeek";
import {
  applyOwnership,
  applyPreset,
  fulfillmentOf,
  isDomainSale,
  isFullAccountSale,
  isRentalSale,
  parsePlanCatalog,
  presetIdOf,
  PRODUCT_PRESETS,
  productTypeKey,
  productTypeLabel,
  showsOwnershipPick,
  slotKindOf,
  slotLabel,
  slotMaxLabel,
  usesGeneratedCode,
  usesPinStock,
  isHostingSitesPreset,
} from "./catalog";
import { bumpNegocio, useRecords, useNegocioResource } from "./Lists";
import { DualMoneyField } from "./AccountFields";
import { moneyBoth, profitPercentOf, roundMoneyAmount, toFxPrice } from "./helpers";
import { useStyles } from "./styles";

const emptyProduct = () => ({
  name: "",
  description: "",
  maxProfiles: 5,
  durationDays: 30,
  imageUrl: "",
  serviceUrl: "",
  isActive: true,
  fulfillmentType: "credentials",
  slotKind: "pin_profile",
  loginPanel: false,
  presetId: "streaming",
  planLabel: "",
  deviceLimit: "",
  originCountry: "",
  warrantyDays: "",
  lotSize: 1,
  parentProductId: "",
  codesBulk: "",
  codeFilters: [{ subject: "", active: true }],
  botSellable: true,
  activationMode: "auto",
});

const formFromProduct = (item) => {
  return {
    ...emptyProduct(),
    ...item,
    presetId: item?.presetId || presetIdOf(item),
      codeFilters:
      Array.isArray(item?.codeFilters) && item.codeFilters.length
        ? item.codeFilters.map((f) => ({
            subject: f.subject || "",
            active: f.active !== false,
          }))
        : [{ subject: "", active: true }],
  };
};

const toPayload = (item) => {
  return {
    name: item.name,
    description: item.description || null,
    imageUrl: item.imageUrl || null,
    serviceUrl: item.serviceUrl || null,
    durationDays: fulfillmentOf(item) === "giftcard" ? 0 : 30,
    maxProfiles:
      slotKindOf(item) === "device"
        ? item.deviceLimit != null && item.deviceLimit !== ""
          ? +item.deviceLimit
          : item.maxProfiles != null
            ? +item.maxProfiles
            : 3
        : item.maxProfiles != null
          ? +item.maxProfiles
          : 5,
    isActive: item.isActive !== false,
    fulfillmentType: item.fulfillmentType || "credentials",
    slotKind: item.slotKind || "pin_profile",
    loginPanel:
      !!(item.loginPanel || item.presetId === "iptv") &&
      !isHostingSitesPreset(item.presetId) &&
      slotKindOf(item) !== "site_login",
    planLabel: item.planLabel || null,
    deviceLimit:
      slotKindOf(item) === "device"
        ? +(item.deviceLimit || item.maxProfiles || 3)
        : null,
    originCountry: fulfillmentOf(item) === "giftcard" ? item.originCountry : null,
    giftCardBrand: fulfillmentOf(item) === "giftcard" ? item.giftCardBrand : null,
    giftCardValue: fulfillmentOf(item) === "giftcard" ? item.giftCardValue : null,
    giftCardCurrency: fulfillmentOf(item) === "giftcard" ? item.giftCardCurrency : null,
    giftCardPrice: fulfillmentOf(item) === "giftcard" && item.giftCardPrice !== "" && item.giftCardPrice != null ? item.giftCardPrice : null,
    giftCardPriceFx: fulfillmentOf(item) === "giftcard" && item.giftCardPriceFx !== "" && item.giftCardPriceFx != null ? item.giftCardPriceFx : null,
    giftCardPriceCurrency: fulfillmentOf(item) === "giftcard" && item.giftCardPriceCurrency === "fx" ? "fx" : "local",
    giftCardCost: fulfillmentOf(item) === "giftcard" && item.giftCardCost !== "" && item.giftCardCost != null ? item.giftCardCost : null,
    giftCardCostFx: fulfillmentOf(item) === "giftcard" && item.giftCardCostFx !== "" && item.giftCardCostFx != null ? item.giftCardCostFx : null,
    giftCardCostCurrency: fulfillmentOf(item) === "giftcard" && item.giftCardCostCurrency === "fx" ? "fx" : "local",
    redeemInstructions: fulfillmentOf(item) === "giftcard" ? item.redeemInstructions : null,

    warrantyDays: item.warrantyDays !== "" && item.warrantyDays != null ? +item.warrantyDays : null,
    lotSize: 1,
    parentProductId: item.parentProductId || null,
    botSellable: item.botSellable !== false,
    activationMode: item.activationMode === "manual" ? "manual" : "auto",
  };
};

const ProductLogo = ({ product, src }) => {
  const classes = useStyles();
  const [broken, setBroken] = useState(false);
  React.useEffect(() => {
    setBroken(false);
  }, [src]);
  if (src && !broken) {
    return (
      <div className={classes.productLogo}>
        <img
          className={classes.productLogoImg}
          src={src}
          alt={product.name || ""}
          onError={() => setBroken(true)}
        />
      </div>
    );
  }
  return <div className={classes.productLogo}>{initialsOf(product.name)}</div>;
};

const ProductCover = ({ product, chip }) => {
  const classes = useStyles();
  if (!product) return null;
  const meta = [productTypeLabel(product, tNegocio), product.planLabel].filter(Boolean).join(" · ");
  return (
    <div className={classes.productCover}>
      <div className={classes.productCoverBanner} />
      {chip ? (
        <div style={{ position: "absolute", top: 10, right: 10, zIndex: 1 }} onClick={(e) => e.stopPropagation()}>
          {chip}
        </div>
      ) : null}
      <div className={classes.productCoverLogoWrap}>
        <ProductMark product={product} size={64} circle link={false} />
      </div>
      <div className={classes.productCoverName}>{product.name}</div>
      {meta ? <div className={classes.productCoverMeta}>{meta}</div> : null}
      <div className={classes.productCoverDesc}>
        {product.description ? <BotMarkup text={product.description} /> : tNegocio("pages.products.noDescription")}
      </div>
      <div className={classes.productCoverStats}>
        <div>
          <strong>{product.accounts || 0}</strong>
          <span>{tNegocio("cols.accounts")}</span>
        </div>
        <div>
          <strong>{product.availableAccounts || 0}</strong>
          <span>{tNegocio("cols.availableAccounts")}</span>
        </div>
        <div>
          <strong>{product.accounts || 0}</strong>
          <span>{tNegocio("cols.records")}</span>
        </div>
      </div>
    </div>
  );
};

export function ProductPeek({ open, product, onClose }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!open) {
      setInfo(null);
      return undefined;
    }
    setInfo(product || null);
    const id = product?.id;
    if (!id) return undefined;
    (async () => {
      try {
        const { data } = await api.get("/negocio/products");
        const found = (data.records || []).find((row) => Number(row.id) === Number(id));
        if (!cancelled && found) setInfo({ ...product, ...found });
      } catch (e) {
        /* the service already has the product snapshot */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, product]);

  return (
    <InfoDialog
      open={!!open && !!info}
      onClose={onClose}
      hero={<ProductCover product={info} />}
      title={info?.name}
      chips={<StatusChip value={info?.isActive !== false ? "active" : "disabled"} />}
      fields={[
        { label: tNegocio("pages.products.ownership.title"), value: isDomainSale(info) ? tNegocio("pages.products.ownership.domain") : isFullAccountSale(info) ? tNegocio("pages.products.ownership.full") : isRentalSale(info) ? tNegocio("pages.products.ownership.rental") : tNegocio(`pages.products.fulfillment.${info?.fulfillmentType || "credentials"}`) },
        { label: tNegocio("pages.products.slotKind"), value: info ? tNegocio(`pages.products.slotKinds.${info.slotKind || "pin_profile"}`) : "" },
        { label: tNegocio("fields.planLabel"), value: info?.planLabel || "—" },
        { label: tNegocio("fields.warrantyDays"), value: info?.warrantyDays || "—" },
        { label: tNegocio("cols.maxProfiles"), value: info?.slotKind === "none" && !usesGeneratedCode(info) ? 1 : (info?.deviceLimit || info?.maxProfiles || 5) },
        { label: tNegocio("cols.accounts"), value: info?.accounts || 0 },
        { label: tNegocio("cols.availableAccounts"), value: info?.availableAccounts || 0 },
        { label: tNegocio("cols.activeAccounts"), value: info?.activeAccounts || 0 },
        usesPinStock(info) ? { label: tNegocio("pages.products.codes.stockLabel"), value: info?.codeStock ?? 0 } : null,
      ]}
    />
  );
}

export default function ProductsPage() {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const history = useHistory();
  const fileRef = useRef(null);
  const [viewMode, setViewMode] = useViewMode("negocio-products", "cards");
  const { records: products, loading, reload } = useRecords("products");
  const { value: savedSettings } = useNegocioResource("settings", async () => {
    const { data } = await api.get("/negocio/settings");
    return data;
  });
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [dialog, setDialog] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [info, setInfo] = useState(null);
  const [plans, setPlans] = useState(parsePlanCatalog());
  const [planDraft, setPlanDraft] = useState("");
  const [planCreateOpen, setPlanCreateOpen] = useState(false);
  const [announce, setAnnounce] = useState(null);
  const [announceSending, setAnnounceSending] = useState(false);

  React.useEffect(() => {
    if (savedSettings) setPlans(parsePlanCatalog(savedSettings.planCatalog));
  }, [savedSettings]);

  const decorated = useMemo(
    () =>
      products.map((p) => ({
        ...p,
        accounts: Number(p.accounts || 0),
        availableAccounts: Number(p.availableAccounts || 0),
        activeAccounts: Number(p.activeAccounts || 0),
      })),
    [products]
  );

  const rows = useMemo(
    () =>
      decorated
        .filter((p) => {
          const okStatus =
            !status ||
            (status === "active" && p.isActive !== false) ||
            (status === "disabled" && p.isActive === false);
          return okStatus && matchesQuery(p, query, [p.name, p.description, p.planLabel, p.parentName]);
        })
        .sort((a, b) => {
          const name = String(a.name || "").localeCompare(String(b.name || ""));
          if (name) return name;
          const parentA = Number(a.parentProductId || a.id);
          const parentB = Number(b.parentProductId || b.id);
          if (parentA !== parentB) return parentA - parentB;
          if (!a.parentProductId && b.parentProductId) return -1;
          if (a.parentProductId && !b.parentProductId) return 1;
          const type = productTypeKey(a).localeCompare(productTypeKey(b));
          if (type) return type;
          return String(a.planLabel || "").localeCompare(String(b.planLabel || ""));
        }),
    [decorated, query, status]
  );
  const pager = usePager(rows);

  const openCreate = () => {
    setLogoFile(null);
    setLogoPreview("");
    setDialog({ ...emptyProduct(), isNew: true });
  };

  const openEdit = (p) => {
    setLogoFile(null);
    setLogoPreview(publicFileUrl(p.imageUrl));
    setDialog({ ...formFromProduct(p), isNew: false });
  };

  const onPickLogo = (file) => {
    if (!file) return;
    const okType = /image\/(jpeg|png|webp)/i.test(file.type);
    if (!okType) {
      toast.error(tNegocio("pages.products.logoType"));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(tNegocio("pages.products.logoSize"));
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const openVariant = (p) => {
    setLogoFile(null);
    setLogoPreview(publicFileUrl(p.imageUrl));
    setDialog({
      ...formFromProduct(p),
      id: undefined,
      isNew: true,
      parentProductId: p.parentProductId || p.id,
      planLabel: "",
    });
  };

  const addPlanInline = async () => {
    const name = String(planDraft || "").trim();
    if (!name) return;
    if (plans.some((item) => item.toLowerCase() === name.toLowerCase())) {
      setDialog((d) => ({ ...d, planLabel: name }));
      setPlanDraft("");
      setPlanCreateOpen(false);
      return;
    }
    const next = [...plans, name];
    try {
      await api.put("/negocio/settings", { planCatalog: next });
      setPlans(next);
      setDialog((d) => ({ ...d, planLabel: name }));
      setPlanDraft("");
      setPlanCreateOpen(false);
      toast.success(tNegocio("toastSaved"));
    } catch (e) {
      toastError(e);
    }
  };

  const saveProduct = async () => {
    if (!dialog) return;
    try {
      const payload = toPayload(dialog);
      if (logoFile) {
        const formData = new FormData();
        formData.append("file", logoFile);
        const { data } = await api.post("/files/upload-single", formData);
        payload.imageUrl = data.path;
      }
      const { data } = dialog.isNew
        ? await api.post("/negocio/products", payload)
        : await api.put(`/negocio/products/${dialog.id}`, payload);
      const productId = data?.id || dialog.id;
      if (productId) {
        await api.put(`/negocio/products/${productId}/code-filters`, {
          filters: (dialog.codeFilters || []).filter((f) => String(f.subject || "").trim()),
        });
      }
      if (usesPinStock(dialog) && String(dialog.codesBulk || "").trim() && productId) {
        await api.post(`/negocio/products/${productId}/codes`, { codesBulk: dialog.codesBulk });
      }
      toast.success(tNegocio("toastSaved"));
      setDialog(null);
      setLogoFile(null);
      bumpNegocio("products");
      reload({ soft: true });
    } catch (e) {
      toastError(e);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/negocio/products/${p.id}`, toPayload({ ...p, isActive: !p.isActive }));
      reload({ soft: true });
    } catch (e) {
      toastError(e);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/negocio/products/${confirm.id}`);
      toast.success(tNegocio("toastSaved"));
      setConfirm(null);
      reload({ soft: true });
    } catch (e) {
      toastError(e);
    }
  };

  const openAnnounce = async (product) => {
    const draft = {
      product,
      message: "",
      mediaUrl: "",
      mediaFile: null,
      onlyActive: true,
      audience: null,
      loadingAudience: true,
    };
    setAnnounce(draft);
    try {
      const { data } = await api.get(`/negocio/products/${product.id}/announce-audience`, {
        params: { onlyActive: 1 },
      });
      setAnnounce((a) => (a && a.product?.id === product.id ? { ...a, audience: data, loadingAudience: false } : a));
    } catch (e) {
      toastError(e);
      setAnnounce((a) => (a && a.product?.id === product.id ? { ...a, loadingAudience: false } : a));
    }
  };

  const reloadAnnounceAudience = async (onlyActive) => {
    if (!announce?.product?.id) return;
    setAnnounce((a) => ({ ...a, onlyActive, loadingAudience: true }));
    try {
      const { data } = await api.get(`/negocio/products/${announce.product.id}/announce-audience`, {
        params: { onlyActive: onlyActive ? 1 : 0 },
      });
      setAnnounce((a) => (a ? { ...a, audience: data, loadingAudience: false } : a));
    } catch (e) {
      toastError(e);
      setAnnounce((a) => (a ? { ...a, loadingAudience: false } : a));
    }
  };

  const sendAnnounce = async () => {
    if (!announce?.product?.id) return;
    const text = String(announce.message || "").trim();
    if (!text) {
      toast.error(tNegocio("pages.products.announce.empty"));
      return;
    }
    setAnnounceSending(true);
    try {
      let mediaUrl = announce.mediaUrl || null;
      if (announce.mediaFile) {
        const formData = new FormData();
        formData.append("file", announce.mediaFile);
        const { data } = await api.post("/files/upload-single", formData);
        mediaUrl = data.path;
      }
      const { data } = await api.post(`/negocio/products/${announce.product.id}/announce`, {
        message: text,
        mediaUrl,
        onlyActive: announce.onlyActive !== false,
      });
      toast.success(
        tNegocio("pages.products.announce.sent")
          .replace("{sent}", String(data?.sent || 0))
          .replace("{total}", String(data?.total || 0))
      );
      if (data?.errors) {
        toast.info(
          tNegocio("pages.products.announce.partialErrors").replace("{n}", String(data.errors))
        );
      }
      setAnnounce(null);
    } catch (e) {
      toastError(e);
    } finally {
      setAnnounceSending(false);
    }
  };

  const renderActions = (p) => (
    <div className={classes.actionCluster}>
      <Tooltip title={tNegocio("pages.products.createAccounts")}>
        <IconButton size="small" color="primary" onClick={stopRow(() => history.push(`/negocio/cuentas?productId=${p.id}&new=1`))}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={tNegocio("pages.products.announce.action")}>
        <IconButton size="small" onClick={stopRow(() => openAnnounce(p))}>
          <AnnouncementIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={tNegocio("edit")}>
        <IconButton size="small" onClick={stopRow(() => openEdit(p))}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={tNegocio("pages.products.createVariant")}>
        <IconButton size="small" onClick={stopRow(() => openVariant(p))}>
          <FileCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={tNegocio("pages.products.remove")}>
        <IconButton size="small" onClick={stopRow(() => setConfirm(p))}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <>
      <PageFrame
        title={tNegocio("pages.products.title")}
        subtitle={tNegocio("pages.products.subtitle")}
        actions={
          <>
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
            <Button variant="contained" color="primary" onClick={openCreate}>
              {tNegocio("new.product")}
            </Button>
          </>
        }
      >
        <div className={classes.toolbar}>
          <FilterChips
            value={status}
            onChange={setStatus}
            options={[
              { value: "", label: tNegocio("filters.all") },
              { value: "active", label: tNegocio("status.active") },
              { value: "disabled", label: tNegocio("status.disabled") },
            ]}
          />
          <SearchField value={query} onChange={setQuery} />
        </div>

        {rows.length && viewMode === "cards" ? (
          <>
          <RecordsCardsGrid dense>
            {pager.slice.map((p) => (
              <RecordCard key={p.id} className={classes.productCard} actions={renderActions(p)} onClick={() => setInfo(p)}>
                <ProductCover
                  product={p}
                  chip={
                    <span
                      onClick={() => toggleActive(p)}
                      style={{ cursor: "pointer" }}
                      title={tNegocio("fields.isActive")}
                    >
                      <StatusChip value={p.isActive !== false ? "active" : "disabled"} />
                    </span>
                  }
                />
              </RecordCard>
            ))}
          </RecordsCardsGrid>
          <NegocioPager
            count={pager.count}
            page={pager.page}
            rowsPerPage={pager.rowsPerPage}
            onChangePage={pager.setPage}
            onChangeRowsPerPage={pager.setRowsPerPage}
          />
          </>
        ) : rows.length ? (
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tNegocio("cols.name")}</TableCell>
                  <TableCell>{tNegocio("cols.plan")}</TableCell>
                  <TableCell>{tNegocio("cols.maxProfiles")}</TableCell>
                  <TableCell>{tNegocio("cols.accounts")}</TableCell>
                  <TableCell>{tNegocio("cols.activeAccounts")}</TableCell>
                  <TableCell>{tNegocio("cols.status")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {pager.slice.map((p) => (
                  <TableRow key={p.id} hover className={classes.clickableRow} onClick={() => setInfo(p)}>
                    <TableCell>
                      <div className={classes.productHead}>
                        <ProductMark product={p} size={32} />
                        <div>
                          <div style={{ fontWeight: 700 }}>{p.name}</div>
                          <div className={classes.captionBlock}>
                            {productTypeLabel(p, tNegocio)}
                            {p.parentName ? ` · ${tNegocio("pages.products.variantOf")} ${p.parentName}` : ""}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{p.planLabel || "—"}</TableCell>
                    <TableCell>{p.slotKind === "none" ? 1 : (p.deviceLimit || p.maxProfiles || 5)}</TableCell>
                    <TableCell>{p.accounts || 0}</TableCell>
                    <TableCell>{p.activeAccounts || 0}</TableCell>
                    <TableCell>
                      <span
                        onClick={() => toggleActive(p)}
                        style={{ cursor: "pointer" }}
                        title={tNegocio("fields.isActive")}
                      >
                        <StatusChip value={p.isActive !== false ? "active" : "disabled"} />
                      </span>
                    </TableCell>
                    <TableCell align="right" className={classes.stickyActions} onClick={(e) => e.stopPropagation()}>
                      {renderActions(p)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <NegocioPager
              count={pager.count}
              page={pager.page}
              rowsPerPage={pager.rowsPerPage}
              onChangePage={pager.setPage}
              onChangeRowsPerPage={pager.setRowsPerPage}
            />
          </div>
        ) : loading ? (
          <LoadingHint />
        ) : (
          <EmptyHint
            title={tNegocio("pages.products.emptyTitle")}
            subtitle={tNegocio("pages.products.empty")}
          />
        )}
      </PageFrame>

      <Dialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        classes={{ paper: dlg.paper }}
      >
        <DialogClose onClose={() => setDialog(null)} />
        <DialogTitle>{dialog?.isNew ? tNegocio("new.product") : tNegocio("edit")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.name")}
            value={dialog?.name || ""}
            onChange={(e) => setDialog((d) => ({ ...d, name: e.target.value }))}
            placeholder={tNegocio("pages.products.namePlaceholder")}
          />
          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("pages.products.preset")}
            value={dialog?.presetId || presetIdOf(dialog)}
            onChange={(e) => setDialog((d) => applyPreset(e.target.value, d))}
            helperText={tNegocio("pages.products.presetHint")}
          >
            {PRODUCT_PRESETS.map((row) => (
              <MenuItem key={row.id} value={row.id}>{row.id === "giftcard" ? "Gift card (Netflix, Xbox, Steam y otras marcas)" : tNegocio(`pages.products.presets.${row.id}`)}</MenuItem>
            ))}
          </TextField>
          {showsOwnershipPick(dialog) ? (
            <>
              <div className={classes.typeChoiceLabel}>{tNegocio("pages.products.ownership.title")}</div>
              <div className={classes.typePick}>
                <button
                  type="button"
                  className={`${classes.typeCard} ${isRentalSale(dialog) ? classes.typeCardOn : ""}`}
                  onClick={() => setDialog((d) => applyOwnership("rental", d))}
                >
                  <strong style={{ display: "inline-flex", alignItems: "center" }}>
                    {tNegocio("pages.products.ownership.rental")}
                    <HintTip text={tNegocio("pages.products.ownership.rentalHint")} />
                  </strong>
                </button>
                <button
                  type="button"
                  className={`${classes.typeCard} ${isDomainSale(dialog) ? classes.typeCardOn : ""}`}
                  onClick={() => setDialog((d) => applyOwnership("domain", d))}
                >
                  <strong style={{ display: "inline-flex", alignItems: "center" }}>
                    {tNegocio("pages.products.ownership.domain")}
                    <HintTip text={tNegocio("pages.products.ownership.domainHint")} />
                  </strong>
                </button>
                <button
                  type="button"
                  className={`${classes.typeCard} ${isFullAccountSale(dialog) ? classes.typeCardOn : ""}`}
                  onClick={() => setDialog((d) => applyOwnership("full", d))}
                >
                  <strong style={{ display: "inline-flex", alignItems: "center" }}>
                    {tNegocio("pages.products.ownership.full")}
                    <HintTip text={tNegocio("pages.products.ownership.fullHint")} />
                  </strong>
                </button>
              </div>
            </>
          ) : fulfillmentOf(dialog) === "giftcard" ? null : (
            <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
              {tNegocio(`pages.products.fulfillment.${fulfillmentOf(dialog)}`)}
              {" · "}
              {tNegocio(`pages.products.slotKinds.${slotKindOf(dialog)}Hint`)}
            </div>
          )}
          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.planLabel")}
            value={dialog?.planLabel || ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__new_plan__") {
                setPlanDraft("");
                setPlanCreateOpen(true);
                return;
              }
              setDialog((d) => ({ ...d, planLabel: v }));
            }}
            helperText={tNegocio("pages.products.planHint")}
          >
            <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
            {plans.map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
            {dialog?.planLabel && !plans.includes(dialog.planLabel) ? (
              <MenuItem value={dialog.planLabel}>{dialog.planLabel}</MenuItem>
            ) : null}
            <MenuItem value="__new_plan__">
              <em>+ {tNegocio("pages.products.addPlanInline")}</em>
            </MenuItem>
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="number"
            label={tNegocio("fields.warrantyDays")}
            value={dialog?.warrantyDays ?? ""}
            onChange={(e) => setDialog((d) => ({ ...d, warrantyDays: e.target.value }))}
            helperText={tNegocio("pages.products.warrantyHint")}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            multiline
            rows={2}
            label={tNegocio("fields.description")}
            value={dialog?.description || ""}
            onChange={(e) => setDialog((d) => ({ ...d, description: e.target.value }))}
            inputProps={{
              style: { resize: "vertical", overflow: "auto" },
            }}
          />
          {fulfillmentOf(dialog) === "giftcard" && <>
            <p>Crea una variante por marca, región y valor. El precio de venta es distinto del saldo de la tarjeta. Carga únicamente códigos de esa variante.</p>
            <TextField fullWidth margin="dense" variant="outlined" label="Marca de la gift card" helperText="Netflix, Xbox, PlayStation, Steam, Amazon…" type="text" value={dialog?.giftCardBrand ?? ""} onChange={e => setDialog(d => ({ ...d, giftCardBrand: e.target.value }))} />
            <TextField fullWidth margin="dense" variant="outlined" label="País o región de canje" helperText="CO, US, MX, ES, EU, LATAM o GLOBAL. Según las condiciones del emisor." type="text" value={dialog?.originCountry ?? ""} onChange={e => setDialog(d => ({ ...d, originCountry: e.target.value }))} />
            <TextField fullWidth margin="dense" variant="outlined" label="Valor nominal de la tarjeta" helperText="Saldo que recibe el cliente; no es el precio de venta." type="number" value={dialog?.giftCardValue ?? ""} onChange={e => setDialog(d => ({ ...d, giftCardValue: e.target.value }))} />
            <TextField fullWidth margin="dense" variant="outlined" label="Moneda de la tarjeta" helperText="COP, USD, EUR, MXN…" type="text" value={dialog?.giftCardCurrency ?? ""} onChange={e => setDialog(d => ({ ...d, giftCardCurrency: e.target.value }))} />
            <DualMoneyField
              label={tNegocio("pages.accounts.purchaseCost")}
              hint={tNegocio("pages.products.giftCardCostHint")}
              localValue={dialog?.giftCardCost}
              fxValue={dialog?.giftCardCostFx}
              currency={dialog?.giftCardCostCurrency || "local"}
              onChange={({ local, fx, currency }) => setDialog((d) => {
                const next = { ...d, giftCardCost: local, giftCardCostFx: fx, giftCardCostCurrency: currency };
                const base = Number(local || 0);
                if (!(base > 0)) return next;
                const sale = roundMoneyAmount(base * (1 + profitPercentOf() / 100));
                const saleCurrency = d?.giftCardPriceCurrency === "fx" ? "fx" : "local";
                return { ...next, giftCardPrice: sale, giftCardPriceFx: toFxPrice(sale), giftCardPriceCurrency: saleCurrency };
              })}
            />
            {Number(dialog?.giftCardCost || 0) > 0 ? (
              <div className={classes.captionBlock} style={{ margin: "-4px 0 8px" }}>
                {tNegocio("pages.accounts.suggestedSale")
                  .replace("{price}", moneyBoth(roundMoneyAmount(Number(dialog.giftCardCost) * (1 + profitPercentOf() / 100)), null))
                  .replace("{pct}", String(profitPercentOf()))}
              </div>
            ) : null}
            <DualMoneyField
              label={tNegocio("pages.accounts.salePrice")}
              hint={tNegocio("pages.products.giftCardPriceHint")}
              localValue={dialog?.giftCardPrice}
              fxValue={dialog?.giftCardPriceFx}
              currency={dialog?.giftCardPriceCurrency || "local"}
              onChange={({ local, fx, currency }) => setDialog((d) => ({ ...d, giftCardPrice: local, giftCardPriceFx: fx, giftCardPriceCurrency: currency }))}
            />
            <TextField fullWidth margin="dense" variant="outlined" label="Instrucciones y restricciones de canje" helperText="Indica cómo canjear, región de la cuenta compatible y condiciones de vigencia del código." type="text" multiline rows={3} value={dialog?.redeemInstructions ?? ""} onChange={e => setDialog(d => ({ ...d, redeemInstructions: e.target.value }))} />
          </>}
          {slotKindOf(dialog) !== "none" || usesGeneratedCode(dialog) ? (
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
              label={usesGeneratedCode(dialog) ? tNegocio("pages.accounts.licenseCount") : slotMaxLabel(dialog, tNegocio)}
              value={dialog?.maxProfiles ?? (usesGeneratedCode(dialog) ? 10 : 5)}
              onChange={(e) => {
                const n = +e.target.value;
                setDialog((d) => ({
                  ...d,
                  maxProfiles: n,
                  deviceLimit: slotKindOf(d) === "device" ? n : d.deviceLimit,
                }));
              }}
            />
          ) : null}
          {usesPinStock(dialog) ? (
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              multiline
              rows={4}
              label={tNegocio("pages.products.codes.title")}
              value={dialog?.codesBulk || ""}
              onChange={(e) => setDialog((d) => ({ ...d, codesBulk: e.target.value }))}
              helperText={
                Number(dialog?.codeStock || 0)
                  ? tNegocio("pages.products.codes.stock").replace("{n}", String(dialog.codeStock))
                  : tNegocio("pages.products.codes.hint")
              }
            />
          ) : null}
          {usesGeneratedCode(dialog) ? (
            <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
              {tNegocio("pages.products.codes.generatedHint")}
            </div>
          ) : null}
          <div className={classes.sectionTitle} style={{ marginTop: 12 }}>{tNegocio("pages.codes.filters")}</div>
          <div className={classes.captionBlock} style={{ marginBottom: 8 }}>{tNegocio("pages.codes.filtersHint")}</div>
          {(dialog?.codeFilters || []).map((filter, index) => (
            <div key={index} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <TextField
                fullWidth
                margin="dense"
                variant="outlined"
                label={tNegocio("pages.codes.subject")}
                value={filter.subject || ""}
                onChange={(e) =>
                  setDialog((d) => {
                    const next = [...(d.codeFilters || [])];
                    next[index] = { ...next[index], subject: e.target.value };
                    return { ...d, codeFilters: next };
                  })
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={filter.active !== false}
                    onChange={(e) =>
                      setDialog((d) => {
                        const next = [...(d.codeFilters || [])];
                        next[index] = { ...next[index], active: e.target.checked };
                        return { ...d, codeFilters: next };
                      })
                    }
                  />
                }
                label={tNegocio("pages.codes.active")}
              />
              <Tooltip title={tNegocio("pages.codes.removeFilter")}>
                <IconButton
                  size="small"
                  onClick={() =>
                    setDialog((d) => ({
                      ...d,
                      codeFilters: (d.codeFilters || []).filter((_, i) => i !== index),
                    }))
                  }
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          ))}
          <Button
            size="small"
            onClick={() =>
              setDialog((d) => ({
                ...d,
                codeFilters: [...(d.codeFilters || []), { subject: "", active: true }],
              }))
            }
          >
            {tNegocio("pages.codes.addFilter")}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(e) => onPickLogo(e.target.files?.[0])}
          />
          <div className={classes.logoDrop} onClick={() => fileRef.current?.click()} role="button" tabIndex={0}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
              <ProductLogo product={dialog || {}} src={logoPreview} />
            </div>
            <div style={{ fontWeight: 700 }}>{tNegocio("fields.logo")}</div>
            <div className={classes.captionBlock}>{tNegocio("fields.logoHint")}</div>
          </div>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.serviceUrl")}
            value={dialog?.serviceUrl || ""}
            onChange={(e) => setDialog((d) => ({ ...d, serviceUrl: e.target.value }))}
            helperText={tNegocio("pages.products.serviceUrlHint")}
            placeholder="https://"
          />
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={dialog?.isActive !== false}
                onChange={(e) => setDialog((d) => ({ ...d, isActive: e.target.checked }))}
              />
            }
            label={tNegocio("fields.isActive")}
          />
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={dialog?.botSellable !== false}
                onChange={(e) => setDialog((d) => ({ ...d, botSellable: e.target.checked }))}
              />
            }
            label={tNegocio("pages.products.botSellable")}
          />
          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("pages.products.activationMode")}
            value={dialog?.activationMode === "manual" ? "manual" : "auto"}
            onChange={(e) => setDialog((d) => ({ ...d, activationMode: e.target.value }))}
            helperText={tNegocio("pages.products.activationModeHint")}
          >
            <MenuItem value="auto">{tNegocio("pages.products.activationAuto")}</MenuItem>
            <MenuItem value="manual">{tNegocio("pages.products.activationManual")}</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={saveProduct}>
            {tNegocio("save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={planCreateOpen}
        onClose={() => setPlanCreateOpen(false)}
        maxWidth="xs"
        fullWidth
        classes={{ paper: dlg.paper }}
      >
        <DialogClose onClose={() => setPlanCreateOpen(false)} />
        <DialogTitle>{tNegocio("pages.products.addPlanInline")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.planLabel")}
            value={planDraft}
            onChange={(e) => setPlanDraft(e.target.value)}
            helperText={tNegocio("pages.products.addPlanInlineHint")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addPlanInline();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={addPlanInline} disabled={!String(planDraft || "").trim()}>
            {tNegocio("pages.products.addPlanInlineBtn")}
          </Button>
        </DialogActions>
      </Dialog>

      <InfoDialog
        open={!!info}
        onClose={() => setInfo(null)}
        hero={<ProductCover product={info} />}
        title={info?.name}
        chips={<StatusChip value={info?.isActive !== false ? "active" : "disabled"} />}
        fields={[
          { label: tNegocio("pages.products.ownership.title"), value: isDomainSale(info) ? tNegocio("pages.products.ownership.domain") : isFullAccountSale(info) ? tNegocio("pages.products.ownership.full") : isRentalSale(info) ? tNegocio("pages.products.ownership.rental") : tNegocio(`pages.products.fulfillment.${info?.fulfillmentType || "credentials"}`) },
          { label: tNegocio("pages.products.slotKind"), value: tNegocio(`pages.products.slotKinds.${info?.slotKind || "pin_profile"}`) },
          { label: tNegocio("fields.planLabel"), value: info?.planLabel || "—" },
          { label: tNegocio("fields.warrantyDays"), value: info?.warrantyDays || "—" },
          { label: tNegocio("cols.maxProfiles"), value: info?.slotKind === "none" && !usesGeneratedCode(info) ? 1 : (info?.deviceLimit || info?.maxProfiles || 5) },
          { label: tNegocio("cols.accounts"), value: info?.accounts || 0 },
          { label: tNegocio("cols.availableAccounts"), value: info?.availableAccounts || 0 },
          { label: tNegocio("cols.activeAccounts"), value: info?.activeAccounts || 0 },
          usesPinStock(info) ? { label: tNegocio("pages.products.codes.stockLabel"), value: info?.codeStock ?? 0 } : null,
        ]}
        extra={
          <RelatedLinks
            items={[
              {
                to: `/negocio/cuentas?productId=${info?.id || ""}`,
                label: tNegocio("related.accounts"),
              },
              {
                to: `/negocio/combos`,
                label: tNegocio("related.combos"),
              },
            ]}
          />
        }
        actions={
          <>
            <Button onClick={() => { setInfo(null); openEdit(info); }}>{tNegocio("edit")}</Button>
            <Button onClick={() => history.push(`/negocio/cuentas?productId=${info?.id || ""}`)}>
              {tNegocio("related.accounts")}
            </Button>
            <Button color="primary" variant="contained" onClick={() => history.push(`/negocio/cuentas?productId=${info?.id || ""}&new=1`)}>
              {tNegocio("pages.products.createAccounts")}
            </Button>
          </>
        }
      />
      <Dialog
        open={!!announce}
        onClose={() => !announceSending && setAnnounce(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        classes={{ paper: dlg.paper }}
      >
        <DialogClose onClose={() => !announceSending && setAnnounce(null)} disabled={announceSending} />
        <DialogTitle>
          {tNegocio("pages.products.announce.title")}
          {announce?.product?.name ? ` · ${announce.product.name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.captionBlock} style={{ marginBottom: 12 }}>
            {tNegocio("pages.products.announce.hint")}
          </div>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={announce?.onlyActive !== false}
                disabled={announceSending || announce?.loadingAudience}
                onChange={(e) => reloadAnnounceAudience(e.target.checked)}
              />
            }
            label={tNegocio("pages.products.announce.onlyActive")}
          />
          <div className={classes.captionBlock} style={{ marginBottom: 12 }}>
            {announce?.loadingAudience ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <CircularProgress size={16} /> {tNegocio("pages.products.announce.loadingAudience")}
              </span>
            ) : (
              tNegocio("pages.products.announce.audience")
                .replace("{total}", String(announce?.audience?.total || 0))
                .replace("{wa}", String(announce?.audience?.whatsapp || 0))
                .replace("{tg}", String(announce?.audience?.telegram || 0))
            )}
          </div>
          <NegocioMessageEditor
            label={tNegocio("pages.products.announce.message")}
            value={announce?.message || ""}
            onChange={(value) => setAnnounce((a) => (a ? { ...a, message: value } : a))}
            showAttach
            mediaUrl={announce?._previewUrl || announce?.mediaUrl || ""}
            onMedia={(file) =>
              setAnnounce((a) => {
                if (!a) return a;
                if (a._previewUrl) {
                  try {
                    URL.revokeObjectURL(a._previewUrl);
                  } catch (e) {
                    /* ignore */
                  }
                }
                const previewUrl = file ? URL.createObjectURL(file) : "";
                return {
                  ...a,
                  mediaFile: file || null,
                  mediaUrl: file ? a.mediaUrl : "",
                  _previewUrl: previewUrl,
                };
              })
            }
            preview
            rows={7}
            varChips={[
              { key: "nombre_cliente", label: "Cliente", sample: "Laura", color: "#00695c", text: "{{nombre_cliente}}" },
              { key: "nombre_servicio", label: "Producto", sample: announce?.product?.name || "Producto", color: "#0288d1", text: "{{nombre_servicio}}" },
              { key: "plan", label: "Plan", sample: announce?.product?.planLabel || "Plan", color: "#6a1b9a", text: "{{plan}}" },
            ]}
            attachAccept="image/*,video/*,application/pdf"
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            disabled={announceSending || announce?.loadingAudience || !(announce?.audience?.total > 0)}
            onClick={sendAnnounce}
            startIcon={announceSending ? <CircularProgress size={16} color="inherit" /> : <AnnouncementIcon />}
          >
            {announceSending
              ? tNegocio("pages.products.announce.sending")
              : tNegocio("pages.products.announce.send")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title={tNegocio("confirmTitle")}
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={runConfirm}
      >
        {tNegocio("confirmBody")}
      </ConfirmationModal>
    </>
  );
}
